import { t } from '@store/I18nStore'
import { rubEquivalent, withRub } from '@constants/currency'

/** Single source for landing plan prices and compare table (mirrors index_8). */
export const LANDING_PRICING = {
  currency: '$',
  plans: { start: 9, pro: 19, max: 39 } as const,
  periods: [
    { m: 1, off: 0 },
    { m: 3, off: 10 },
    { m: 6, off: 15 },
    { m: 12, off: 20 },
  ] as const,
  maxOps: 100,
}

export type CompareCell = boolean | string
export type CompareGroup = { g: string }
export type CompareRow = [string, CompareCell, CompareCell, CompareCell] | CompareGroup

type CompareLimits = Partial<Record<'start' | 'pro' | 'max', {
  channels: number | null
  regionsPerChannel: number | null
  commonTemplateSections: number | null
  personalTemplateSections: number | null
}>>

/** Таблица сравнения; лимиты, которые прислал бэк (GET /api/billing/plans), заменяют значения по умолчанию. */
export function getLandingCompare(limits: CompareLimits = {}): CompareRow[] {
  const unlimited = t('billing.limits.no_limits')
  const bySub = t('landing.compare.values.by_subscription')
  const upTo = (n: number) => t('landing.compare.values.up_to', { count: n })
  const count = (id: 'start' | 'pro' | 'max', key: 'channels' | 'regionsPerChannel', fallback: string, fmt: (n: number) => string) => {
    const v = limits[id]?.[key]
    if (v === undefined) return fallback
    return v === null ? unlimited : fmt(v)
  }
  const templates = (id: 'start' | 'pro' | 'max', fallback: string) => {
    const lim = limits[id]
    if (!lim) return fallback
    const part = (n: number | null) => (n === null ? unlimited : String(n))
    if (lim.commonTemplateSections === null && lim.personalTemplateSections === null) return unlimited
    if (lim.personalTemplateSections !== null && lim.personalTemplateSections <= 0) return part(lim.commonTemplateSections)
    return `${part(lim.commonTemplateSections)} + ${part(lim.personalTemplateSections)}`
  }
  return [
    { g: t('landing.compare.groups.channels_regions') },
    [t('landing.compare.rows.channels'), count('start', 'channels', '1', String), count('pro', 'channels', '3', String), count('max', 'channels', unlimited, String)],
    [t('landing.compare.rows.regions_per_channel'), count('start', 'regionsPerChannel', upTo(3), upTo), count('pro', 'regionsPerChannel', upTo(5), upTo), count('max', 'regionsPerChannel', unlimited, upTo)],
    [t('landing.features.site_and_telegram_one_console'), true, true, true],
    { g: t('landing.compare.groups.operators') },
    [t('landing.compare.rows.operators'), bySub, bySub, bySub],
    [t('landing.compare.rows.team_roles'), true, true, true],
    [t('landing.compare.rows.shared_archive'), true, true, true],
    [t('landing.compare.rows.phrase_templates'), templates('start', '3 + 3'), templates('pro', '6 + 6'), templates('max', unlimited)],
    { g: t('nav.widget') },
    [
      t('nav.customization'),
      t('billing.features.basic'),
      t('billing.customization.extended'),
      t('billing.customization.full'),
    ],
    [t('landing.compare.rows.files'), true, true, true],
    [
      t('landing.compare.rows.branding'),
      t('landing.compare.values.brand_stays'),
      t('landing.compare.values.brand_removable'),
      t('landing.compare.values.brand_removable'),
    ],
    { g: t('landing.compare.groups.misc') },
    [t('landing.compare.rows.encryption'), true, true, true],
    [
      t('nav.support'),
      t('landing.compare.values.standard'),
      t('landing.compare.values.standard'),
      t('landing.compare.values.priority'),
    ],
  ]
}

export function formatMoney(n: number, currency = LANDING_PRICING.currency): string {
  const r = Math.round(n * 100) / 100
  return currency + String(r).replace('.', ',')
}

/** «$9 / 765 ₽» — цена с рублёвым эквивалентом (лендинг считает в USD). */
export function formatMoneyDual(n: number): string {
  return withRub(formatMoney(n), n, 'USD')
}

/** Только рублёвая часть для отдельного элемента рядом с крупной ценой: «765 ₽». */
export function rubPart(n: number): string {
  return rubEquivalent(n, 'USD')
}

export function pluralRu(n: number, one: string, few: string, many: string): string {
  const m100 = n % 100
  if (m100 > 10 && m100 < 20) return many
  const m10 = n % 10
  if (m10 === 1) return one
  if (m10 >= 2 && m10 <= 4) return few
  return many
}
